import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { AppContext, API } from "./context";
import { useSounds } from "./hooks/useSounds";
import Header from "./components/Header";
import LaunchBanner from "./components/PrelaunchLaunchBanner";
import Footer from "./components/Footer";
import MobileBottomNav from "./components/MobileBottomNav";
import { isPrelaunchUnlocked } from "./components/PrelaunchGate";
import NPCGuide from "./components/NPCGuide";
import FabTidyToggle from "./components/FabTidyToggle";
import GiftInbox from "./components/GiftInbox";
import PendingFriendConnect from "./components/PendingFriendConnect";
import PresenceHeartbeat from "./components/PresenceHeartbeat";
import SupportViewingBanner from "./components/support/SupportViewingBanner";
import RouteSEO from "./components/RouteSEO";
import PreviewSessionBanner from "./components/PreviewSessionBanner";
import PreviewBannerBar from "./components/PreviewBannerBar";
import { getPreviewFromLocation, buildPreviewUser } from "./utils/previewScenario";
import { GuestProvider, GuestBanner } from "./components/GuestSystem";
import GuestProgressChip from "./components/GuestProgressChip";
import GuestStarMilestones from "./components/GuestStarMilestones";
import GuestCoach from "./components/GuestCoach";
import ScrollToTop from "./components/ScrollToTop";
import AppRouter from "./AppRouter";
import { stopLawrence } from "./lib/lawrenceVoice";
import { clearAllGameSaves } from "./utils/gameSave";
import ErrorBoundary from "./components/ErrorBoundary";
import OfflineBanner from "./components/OfflineBanner";
import TrialCountdownBanner from "./components/TrialCountdownBanner";
import PostTrialOfferModal from "./components/PostTrialOfferModal";
import PostLaunchOfferModal from "./components/PostLaunchOfferModal";
import LevelUpCelebration from "./components/LevelUpCelebration";
import { installStarEvents } from "./lib/starEvents";
import GlobalBackButton from "./components/GlobalBackButton";
import CookieBanner from "./components/CookieBanner";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useRetryQueue } from "./hooks/useRetryQueue";
import { enqueue as enqueueRetry } from "./utils/retryQueue";
import InstallPrompt from "./components/InstallPrompt";
import HelpChatWidget from "./components/HelpChatWidget";
import ReportProblem from "./components/ReportProblem";
import IncomingVoicePopup from "./components/IncomingVoicePopup";
import TimezoneCapture from "./components/TimezoneCapture";import { useInstallPrompt } from "./hooks/useInstallPrompt";
import { identifyUser, resetIdentity, captureError, analytics } from "./utils/analytics";
import { setReaderPrefs, setReaderContext, setAutoReadPrefs, setOptionPhrasing } from "./lib/lawrenceVoice";
import { getActiveLearnerId, reconcileActiveLearner } from "./hooks/useActiveLearner";
import UniverseLayout from "./components/UniverseLayout";
import PreviewExpiryWatcher from "./components/PreviewExpiryWatcher";
import EmailConfirmGate from "./components/EmailConfirmGate";
import SupportBanner from "./components/support/SupportBanner";
import VerifiedCelebration from "./components/VerifiedCelebration";
import PauseBanner from "./components/PauseBanner";
import { IsaiahProvider, IsaiahFloatingCompanion } from "./components/isaiah";
import IsaiahContextActivator from "./components/isaiah/IsaiahContextActivator";
import PlatformTour from "./components/PlatformTour";
import WelcomeNewUserModal from "./components/WelcomeNewUserModal";
import KidOnboarding from "./components/KidOnboarding";
import FirstMissionPrompt from "./components/FirstMissionPrompt";
import PetCompanion from "./components/PetCompanion";
import WhatsNewModal from "./components/WhatsNewModal";
import GiftCelebrationModal from "./components/GiftCelebrationModal";
import ParentalConsentGate from "./components/ParentalConsentGate";
import UpgradeNudge from "./components/UpgradeNudge";
import ChildConsentBanner from "./components/ChildConsentBanner";
import SoloExplorerProfileNudge from "./components/SoloExplorerProfileNudge";
import ProfileCompletionToast from "./components/ProfileCompletionToast";
import AcademyContextBanner from "./components/AcademyContextBanner";
import AcademyExitButton from "./components/AcademyExitButton";
import AcademyChromeToggle from "./components/AcademyChromeToggle";
import FocusModeFlash from "./components/FocusModeFlash";
import { setFocusActive } from "./lib/academyFocus";
import FocusModeButton from "./components/learn/FocusModeButton";
import { Toaster, toast } from "sonner";

// Configure axios to always send cookies (HTTP-only auth_token)
axios.defaults.withCredentials = true;

// Session storage helpers for Remember Me
const TOKEN_KEY = "isaiah_token";

// Attach stored token to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  if (token && token !== "demo-token") {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
const USER_KEY = "isaiah_user";
const REMEMBER_KEY = "isaiah_remember";

// Queue failed write requests when offline for auto-retry on reconnect, AND
// auto-retry transient GET failures (cold-start / rollout / brief blips) with
// exponential backoff so the app SELF-HEALS instead of showing empty/error
// screens. This is the fix for "load the site right after deploy → no data,
// then it appears a couple minutes later": data GETs now keep retrying quietly.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config;
    // Only queue if offline and it's a network error (no response from server)
    if (!navigator.onLine && !error.response && cfg) {
      enqueueRetry(cfg);
    }
    const status = error.response?.status;
    const method = (cfg?.method || "get").toLowerCase();
    // Retry ONLY safe idempotent GETs, and ONLY on transient conditions:
    // a network error / timeout (no response) or a gateway status (502/503/504).
    // Never retry 4xx (auth/not-found won't heal) — those bubble up immediately.
    const transient = cfg && !error.response ? true : (status === 502 || status === 503 || status === 504);
    if (cfg && method === "get" && transient && navigator.onLine) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= 7) {
        const delay = Math.min(600 * 2 ** (cfg.__retryCount - 1), 8000); // 0.6,1.2,2.4,4.8,8,8,8s (~33s total)
        await new Promise((r) => setTimeout(r, delay));
        return axios(cfg);
      }
    }
    return Promise.reject(error);
  }
);

// Fire an app-wide "stars earned" signal after any star-granting API call so the
// global Level-Up celebration can re-check the learner's level.
installStarEvents();

function getStoredToken() {
  // Always read from localStorage (shared across all tabs of the same
  // origin). We still fall through to sessionStorage so users who were
  // signed in BEFORE this change don't get booted, their next refresh
  // will migrate them into localStorage via storeSession().
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
}
function getStoredUser() {
  try {
    const saved = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
}
function storeSession(token, user, remember) {
  // Always persist to localStorage so the session is visible to every
  // tab of the same origin (fixes the "new tab asks me to sign in again"
  // bug). `remember` is kept as a UX signal: when the user unchecks it
  // we also register a beforeunload hook elsewhere to clear on browser
  // close. `sessionStorage` is no longer used for auth, it doesn't
  // propagate across tabs by design.
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (remember) localStorage.setItem(REMEMBER_KEY, "true");
  else localStorage.removeItem(REMEMBER_KEY);
}
function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(REMEMBER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

// Routes where we keep the chrome visually present (logo, header menu,
// footer) for branding but freeze all interaction. Visitors see the brand
// but can only fill out the waitlist form , every other element is
// pointer-events: none so links/buttons are decorative-only.
const LOCKDOWN_PATHS = new Set([
  "/waitlist",
  "/waitlist-confirmation",
]);

// When a child opens Professor Owl / Upload & Learn / Whiteboard FROM the
// Academy (context flag set), treat those tool pages as "in Academy" so the
// global site menu is hidden, the child stays fully inside the learning space.
function academyToolInFocus(pathname) {
  const tools = ["/learn/tutor", "/learn/upload", "/learn/whiteboard"];
  if (!tools.some((p) => pathname === p || pathname.startsWith(p + "/"))) return false;
  try { return sessionStorage.getItem("academy_context_active") === "1"; } catch { return false; }
}

function AppShell({ user, canShowInstall, triggerInstall, dismissInstall, dismissInstallForever, isOnline, wasOffline, retryQueue, showWhatsNew, setShowWhatsNew }) {
  const location = useLocation();

  // GLOBAL AUDIO SAFETY NET — the instant the route changes (opening or exiting
  // a lesson, a quiz step that navigates, leaving any page), stop ALL narration
  // so a voice can never keep reading a screen the child has already left. This
  // one effect covers every learning feature at once. stopLawrence() also
  // cancels the browser SpeechSynthesis fallback, so both engines go silent.
  useEffect(() => {
    try { stopLawrence(); } catch { /* noop */ }
    try { if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel(); } catch { /* noop */ }
  }, [location.pathname]);

  // Academy "Focus" toggle, the global chrome is hidden by default inside the
  // Academy so kids stay focused, but a grown-up can reveal the site header
  // (to switch who's learning or leave) via the floating Focus button. The
  // revealed state persists WHILE inside the Academy (so the header stays up
  // long enough to actually use it), and resets to hidden, the default, the
  // moment they leave the Academy.
  const [chromeRevealed, setChromeRevealed] = useState(false);
  // Brief full-screen "Focus Mode" flash when a learner hides the menu.
  const [focusFlash, setFocusFlash] = useState(false);
  const inAcademyPath = location.pathname.startsWith("/learn/academy")
    || location.pathname.startsWith("/academy/lesson");
  useEffect(() => { if (!inAcademyPath) setChromeRevealed(false); }, [inAcademyPath]);
  // True when we're on a locked-down route AND the device hasn't been
  // unlocked via the secret /home URL. Chrome stays visible; we just
  // freeze interactions and hide non-essential floating widgets.
  const lockedDown = LOCKDOWN_PATHS.has(location.pathname) && !isPrelaunchUnlocked();
  // CSS: every clickable element inside the chrome becomes a pretty
  // ornament (no hover, no pointer, no navigation). The form area sits
  // outside this wrapper so it stays fully interactive.
  const lockedStyle = lockedDown ? { pointerEvents: "none", userSelect: "none" } : undefined;

  // 🎓 Academy lockdown, when a kid is inside the Learning Forest Academy
  // (and EVERY sub-page below /learn/academy/), the global app chrome
  // (logo, Learn/Arcade/Adventures/Create/Parent Corner, sound, music,
  // founder pill, copy/share, dashboard, sticker dropdown, bottom mobile
  // nav, marketing footer) is COMPLETELY hidden so the kid stays focused
  // on lessons. The ONLY way out is the Exit Academy button (rendered
  // by the floating <AcademyExitButton/> below, present on every
  // /learn/academy/* route).
  const inAcademy = location.pathname.startsWith("/learn/academy")
    || location.pathname.startsWith("/academy/lesson")
    || (location.pathname === "/art-studio" && location.search.includes("from=academy"))
    || academyToolInFocus(location.pathname);
  // Keep the Focus-streak flag in sync: Focus Mode is ON whenever a learner is
  // inside the Academy with the menu hidden (the default deep-work state).
  useEffect(() => { setFocusActive(inAcademy && !chromeRevealed); }, [inAcademy, chromeRevealed]);

  // Backend "Reconnecting…" watcher. When any API call reports the server as
  // unreachable (Cloudflare/cold-start blip) we show a friendly banner and poll
  // the health endpoint every 4s until it recovers, then let pages refetch.
  const [serverDown, setServerDown] = useState(false);
  const serverPollRef = useRef(null);
  const serverConfirmingRef = useRef(false);
  const appMountAt = useRef(Date.now());
  // Bumping this key remounts the routed page on recovery so its data silently
  // re-fetches — the learner doesn't have to tap or refresh anything.
  const [recoveryKey, setRecoveryKey] = useState(0);
  useEffect(() => {
    // A single lightweight health ping with a short timeout.
    const pingHealthy = async (timeoutMs = 5000) => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), timeoutMs);
        const res = await fetch(`${API}/`, { signal: ctrl.signal, cache: "no-store" });
        clearTimeout(t);
        return res.ok;
      } catch { return false; }
    };
    const recover = () => {
      setServerDown(false);
      serverConfirmingRef.current = false;
      if (serverPollRef.current) { clearInterval(serverPollRef.current); serverPollRef.current = null; }
      window.dispatchEvent(new Event("iw-server-recovered"));
      setRecoveryKey((k) => k + 1);
      toast.success("Back online — refreshed!", { description: "Your connection is back and the page is up to date." });
    };
    const checkHealth = async () => { if (await pingHealthy(6000)) recover(); };
    // A failed/slow/aborted request fires this. Do NOT alarm the user on the
    // strength of one failure. Insurance layers, in order:
    //  1. Grace period — never show during the first 8s of load (initial page
    //     load races/cancels many requests; those are normal, not an outage).
    //  2. Confirm with THREE health pings spread over ~15s before showing the
    //     banner, so a brief backend blip/restart never flashes "reconnecting"
    //     at the user — only a genuine, sustained outage does.
    const onDown = async () => {
      if (serverDown || serverConfirmingRef.current) return;
      if (Date.now() - appMountAt.current < 8000) return; // startup grace period
      serverConfirmingRef.current = true;
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const first = await pingHealthy(6000);
      if (first) { serverConfirmingRef.current = false; return; }
      await sleep(1500);
      const second = await pingHealthy(6000);
      if (second) { serverConfirmingRef.current = false; return; }
      await sleep(2500);
      const third = await pingHealthy(6000);
      serverConfirmingRef.current = false;
      if (third) return; // recovered during the ~15s window — never show the banner
      setServerDown(true);
      if (!serverPollRef.current) serverPollRef.current = setInterval(checkHealth, 4000);
    };
    window.addEventListener("iw-server-unreachable", onDown);
    return () => {
      window.removeEventListener("iw-server-unreachable", onDown);
      if (serverPollRef.current) { clearInterval(serverPollRef.current); serverPollRef.current = null; }
    };
  }, [serverDown]);
  const retryServer = () => {
    fetch(`${API}/`, { cache: "no-store" })
      .then((r) => { if (r.ok) { setServerDown(false); serverConfirmingRef.current = false; if (serverPollRef.current) { clearInterval(serverPollRef.current); serverPollRef.current = null; } window.dispatchEvent(new Event("iw-server-recovered")); setRecoveryKey((k) => k + 1); toast.success("Back online — refreshed!", { description: "Your connection is back and the page is up to date." }); } })
      .catch(() => {});
  };
  // Where the global Focus Mode button appears, across ALL learning tools.
  const inLearnArea = ["/learn", "/academy", "/art-studio", "/arcade", "/quiz", "/games"]
    .some((p) => location.pathname === p || location.pathname.startsWith(p + "/") || location.pathname.startsWith(p));
  const hideChrome = inAcademy && !chromeRevealed;
  const academyChromeStyle = hideChrome
    ? { display: "none" }
    : undefined;
  const academyChromeAttrs = hideChrome
    ? { "aria-hidden": "true", "data-academy-locked": "true" }
    : {};

  return (
    <div className="app-universe">
      <GuestBanner />
      {user && <SupportBanner />}
      {user && <EmailConfirmGate />}
      {user && <VerifiedCelebration />}
      {user && <PauseBanner />}
      <TrialCountdownBanner user={user} />
      {user && <PreviewExpiryWatcher />}
      {user && <PostTrialOfferModal />}
      {user && <PostLaunchOfferModal />}
      {user && <LevelUpCelebration user={user} />}
      <OfflineBanner isOnline={isOnline} wasOffline={wasOffline} retryQueue={retryQueue} serverDown={serverDown} onRetryServer={retryServer} />
      {/* On lockdown pages (/waitlist) the chrome is decorative, every nav
          link freezes via pointer-events: none. The ONE interactive escape
          is the Login button, which marks itself with pointerEvents: auto
          inside Header.js so already-approved founders can still sign in.
          We intentionally OMIT aria-hidden here so assistive tech still
          announces the Login button; pointer-events handles the rest. */}
      <div style={lockedStyle || academyChromeStyle} {...academyChromeAttrs}>
        <LaunchBanner />
        <Header />
      </div>
      {!lockedDown && !inAcademy && <GlobalBackButton />}
      <main className="main-universe">
        <a href="#main-content" className="skip-to-content">Skip to content</a>
        <ErrorBoundary level="app" section="Isaiah's World">
          <ParentalConsentGate>
            <UniverseLayout>
              <AppRouter key={recoveryKey} />
            </UniverseLayout>
          </ParentalConsentGate>
        </ErrorBoundary>
      </main>
      <div style={lockedStyle || academyChromeStyle} aria-hidden={(lockedDown || hideChrome) ? "true" : undefined}>
        <Footer />
        <MobileBottomNav />
      </div>
      {inAcademy && <AcademyExitButton />}
      {inAcademy && <AcademyChromeToggle revealed={chromeRevealed} onToggle={() => setChromeRevealed((v) => {
        const next = !v;
        // Menu hidden (next=false) => Focus Mode ON. Flash + track for streaks.
        setFocusActive(!next);
        if (v && !next) setFocusFlash(true);
        return next;
      })} />}
      {focusFlash && <FocusModeFlash onDone={() => setFocusFlash(false)} />}
      {inLearnArea && !inAcademy && !lockedDown && <FocusModeButton />}
      <CookieBanner />
      {!lockedDown && (
        <>
          <InstallPrompt
            canShow={canShowInstall && !user?.isDemo}
            onInstall={triggerInstall}
            onDismiss={dismissInstall}
            onNeverShow={dismissInstallForever}
          />
          <NPCGuide />
          <ReportProblem />
          <FabTidyToggle />
          <GiftInbox />
          <PendingFriendConnect />
          <PresenceHeartbeat />
          <SupportViewingBanner />
          <IsaiahFloatingCompanion />
          <IsaiahContextActivator />
          <PlatformTour />
          <WelcomeNewUserModal />
          <KidOnboarding />
          <FirstMissionPrompt />
          <GuestProgressChip />
          <GuestStarMilestones />
          <GuestCoach />
          <GiftCelebrationModal />
          <UpgradeNudge />
          <ChildConsentBanner />
          <SoloExplorerProfileNudge />
          <ProfileCompletionToast />
          <AcademyContextBanner />
          {showWhatsNew && <WhatsNewModal onClose={() => setShowWhatsNew(false)} />}
        </>
      )}
      {user && <IncomingVoicePopup user={user} />}
      {user && <TimezoneCapture user={user} />}
      <Toaster position="top-center" richColors theme="dark" toastOptions={{
        style: { background: "rgba(15,20,40,0.95)", border: "1px solid rgba(255,255,255,0.1)" }
      }} />
    </div>
  );
}

export default function App() {
  // Set theme mode on mount
  useEffect(() => {
    const mode = localStorage.getItem("isaiah-theme-mode") || "light";
    const resolved = mode === "auto" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : mode;
    document.documentElement.setAttribute("data-theme-mode", resolved);
  }, []);

  // Load the GLOBAL multiple-choice voice standard once on boot so EVERY
  // learning tool speaks options the verified-correct way. The verified default
  // is baked into lawrenceVoice, so a failed/slow fetch changes nothing.
  useEffect(() => {
    axios.get(`${API}/voice/mc-standard`)
      .then((r) => { if (r.data?.template) setOptionPhrasing(r.data.template, r.data.empty_template); })
      .catch(() => {});
  }, []);

  const DEMO_USER = {
    id: "demo-explorer",
    email: "explorer@isaiahsworld.com",
    name: "Explorer",
    role: "explorer",
    age: 8,
    avatar: "default",
    isDemo: true,
  };

  // Preview session (admin Preview Studio iframe or shared ?preview= link):
  // resolve the persona SYNCHRONOUSLY so the very first render already IS the
  // persona. Doing this only in a useEffect (below) leaves `user` null for one
  // render, which let route-guards / page auth-gates fire and bounce a
  // previewed persona to /signup. Building it here kills that race.
  const previewInit = getPreviewFromLocation();
  const previewInitUser = previewInit
    ? buildPreviewUser(previewInit.scenario, previewInit.grade, previewInit.premium)
    : null;
  // A guest persona must behave like a real guest-roam session: give it the
  // demo token so the 5-minute GuestSystem + guest route-guards engage.
  if (previewInitUser?.isDemo) {
    try { sessionStorage.setItem(TOKEN_KEY, "demo-token"); } catch (_e) { /* ignore */ }
  }
  const storedUser = previewInitUser || getStoredUser();
  const [user, setUser] = useState(storedUser);
  const [token, setToken] = useState(previewInitUser ? (previewInitUser.isDemo ? "demo-token" : null) : getStoredToken());
  // Auth bootstrap state, tracks whether /api/auth/me has resolved (or
  // failed / timed out) so route wrappers and App-level children can render
  // a clean loading state instead of flashing a partial tree. Hoisted here
  // per testing-agent suggestion (iter 346) so the 10s escape hatch applies
  // uniformly regardless of which route wrapper (ProtectedRoute / PreviewRoute /
  // unwrapped) the user lands on.
  //
  //   status === "idle"       → no token, render app immediately
  //   status === "loading"    → token exists, /auth/me in flight
  //   status === "ready"      → /auth/me succeeded
  //   status === "expired"    → /auth/me failed or timed out → clear token
  const [authStatus, setAuthStatus] = useState(() => {
    if (previewInitUser) return "ready";
    const hasToken = !!getStoredToken();
    const hasUser = !!getStoredUser();
    // We already have a cached user + token → show the app immediately;
    // the background /auth/me refresh will just rehydrate fields.
    if (hasToken && hasUser) return "ready";
    if (hasToken) return "loading";
    return "idle";
  });
  const sounds = useSounds();
  const { isOnline, wasOffline } = useOnlineStatus();
  const retryQueue = useRetryQueue();
  const { canShow: canShowInstall, triggerInstall, dismiss: dismissInstall, dismissForever: dismissInstallForever } = useInstallPrompt();
  const [showWhatsNew, setShowWhatsNew] = useState(false);

  // Show "What's New" modal after user is loaded (not for demo users)
  useEffect(() => {
    if (user && token && token !== "demo-token" && !user.isDemo) {
      // Small delay so it doesn't compete with other modals
      const timer = setTimeout(() => setShowWhatsNew(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setShowWhatsNew(false);
    }
  }, [user, token]);

  // Global error monitoring, capture unhandled errors to PostHog
  useEffect(() => {
    const onError = (event) => captureError(event.error || event.message, { source: "window.onerror" });
    const onRejection = (event) => captureError(event.reason, { source: "unhandledrejection" });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  // Verify token on load, with a 10s escape hatch so a hung API doesn't
  // leave the user staring at a spinner forever.
  useEffect(() => {
    // Preview session (admin Preview Studio iframe or shared ?preview= link):
    // render as a synthetic persona WITHOUT touching the real localStorage
    // session. Takes precedence over any stored admin token.
    const preview = getPreviewFromLocation();
    if (preview) {
      setUser(buildPreviewUser(preview.scenario, preview.grade, preview.premium));
      setToken(null);
      setAuthStatus("ready");
      return;
    }
    const savedToken = getStoredToken();
    if (!savedToken || savedToken === "demo-token") { setAuthStatus("idle"); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      // /auth/me hasn't answered in 10s. DON'T nuke the session, that
      // re-engages PrelaunchGate and bounces the user to the waitlist
      // mid-walk. Just mark auth as ready with the cached user blob.
      // A real 401 from /auth/me will fall through to the .catch and
      // clear the session for us.
      console.warn("[auth] /api/auth/me >10s, keeping cached session and marking auth ready");
      setAuthStatus("ready");
    }, 10000);
    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${savedToken}` },
      withCredentials: true,
    })
      .then(res => {
        if (cancelled) return;
        clearTimeout(timer);
        const userData = res.data;
        setUser(userData);
        setToken(savedToken);
        const remember = !!localStorage.getItem(TOKEN_KEY);
        storeSession(savedToken, userData, remember);
        setAuthStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(timer);
        // Only clear the session on a REAL 401 (token is actually rejected).
        // For everything else, network blip, 5xx, CORS, KEEP the stored
        // user blob so PrelaunchGate's "authed" bypass keeps holding. The
        // previous behaviour (clearSession on any error) silently logged
        // users out during a long Academy walk-through whenever /auth/me
        // had a transient hiccup, re-engaging the prelaunch gate and
        // rendering the waitlist landing in-place at /learn/academy/*
        // routes (iter429 audit findings).
        const status = err && err.response && err.response.status;
        if (status === 401 || status === 403) {
          clearSession();
          setUser(null);
          setToken(null);
          setAuthStatus("expired");
        } else {
          // Transient failure, keep the cached user so the UI doesn't
          // visibly log itself out. Mark auth as "ready" so downstream
          // components don't sit in a loading state.
          console.warn("[auth] /api/auth/me transient failure, keeping cached session", err && err.message);
          setAuthStatus("ready");
        }
      });
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  // SELF-HEAL the active learner. On boot (and whenever the account changes),
  // confirm the stored "learning as" pointer still maps to a real family member.
  // If it points at a deleted/phantom/stale profile, silently reset to self so
  // it can never poison the Learning Forest, Upload & Learn, or Quiz Battle.
  useEffect(() => {
    const selfId = user?.profile_id || user?.id;
    if (!selfId || user?.isDemo || user?.preview_session) return;
    let alive = true;
    axios.get(`${API}/family/me`)
      .then((r) => {
        if (!alive) return;
        const kids = (r.data?.children || []).map((c) => c.id).filter(Boolean);
        const healed = reconcileActiveLearner([selfId, user?.id, user?.profile_id, ...kids]);
        if (healed) console.warn("[active-learner] cleared a stale/phantom learner pointer — reset to self");
      })
      .catch(() => { /* transient — leave the pointer, next boot retries */ });
    return () => { alive = false; };
  }, [user]);

  // Reader Voice, load the effective narration voices (reading + games) for
  // whoever is the ACTIVE learner (a per-child override, else the family
  // default) and apply them app-wide. Reloads when the active learner switches.
  useEffect(() => {
    const acctId = user?.id || user?.email;
    if (!acctId || user?.isDemo || user?.preview_session) return;
    let alive = true;
    const load = () => {
      const childId = getActiveLearnerId(acctId) || acctId;
      axios.get(`${API}/parent/reader-prefs/${encodeURIComponent(acctId)}/${encodeURIComponent(childId)}`)
        .then((r) => { if (alive && r.data) setReaderPrefs({ reading: r.data.reading, games: r.data.games }); })
        .catch(() => {});
      axios.get(`${API}/parent/auto-read/${encodeURIComponent(acctId)}/${encodeURIComponent(childId)}`)
        .then((r) => { if (alive && r.data) setAutoReadPrefs({ autoRead: r.data.auto_read_enabled, readerLocked: r.data.reader_locked }); })
        .catch(() => {});
    };
    load();
    window.addEventListener("active-learner-changed", load);
    return () => { alive = false; window.removeEventListener("active-learner-changed", load); };
  }, [user]);

  // Voice Per Subject, switch the narration context by route so games speak
  // in the "games" voice and everything else uses the "reading" voice.
  useEffect(() => {
    const p = location.pathname;
    const isGames = p.startsWith("/arcade") || p.startsWith("/adventures") || p.includes("/game");
    setReaderContext(isGames ? "games" : "reading");
  }, [location.pathname]);

  const login = (newToken, userData, remember = false) => {
    storeSession(newToken, userData, remember);
    setToken(newToken);
    setUser(userData);
    sounds.playSound("levelUp");
    identifyUser(userData.id || userData.email, { name: userData.name, role: userData.role, age: userData.age });
    analytics.login(userData.role === "explorer" ? "demo" : "email");
    // Hydrate the full profile (equipped avatar, frame, flags) from /auth/me so
    // the header shows the user's real picture immediately after login.
    setTimeout(() => { try { refreshUser(); } catch { /* ignore */ } }, 0);
  };

  // Update the in-memory + persisted user after profile or activity changes.
  //   - refreshUser(newData)  → write-through update with new user payload
  //   - refreshUser()         → safe re-fetch from /auth/me for real users.
  //                             Demo/guest users skip the fetch (no account exists).
  // Calling with no args NEVER nulls out the user (that was a bug that logged
  // guests out after spinning the daily wheel).
  const refreshUser = async (userData) => {
    if (userData) {
      setUser(userData);
      const remember = !!localStorage.getItem(TOKEN_KEY);
      const currentToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
      if (currentToken) storeSession(currentToken, userData, remember);
      return;
    }
    // No arg → refetch for real users only
    const currentToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    if (!currentToken || currentToken === "demo-token") return;  // guests: noop
    try {
      const r = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        withCredentials: true,
      });
      if (r.data) {
        setUser(r.data);
        const remember = !!localStorage.getItem(TOKEN_KEY);
        storeSession(currentToken, r.data, remember);
      }
    } catch {
      /* ignore transient errors, don't log the user out */
    }
  };

  // Live User Sync — keep a logged-in user's session in step with server-side
  // changes an admin makes (tier / status / entitlements). Server reads are
  // already instant; this pulls a fresh /auth/me on tab focus and every 60s so
  // the user's OWN screen reflects an upgrade/downgrade/suspension without a
  // manual refresh. Skips demo/guest sessions.
  useEffect(() => {
    const currentToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    if (!currentToken || currentToken === "demo-token") return;
    const sync = () => { refreshUser(); };
    const onFocus = () => sync();
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(sync, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps


  const loginAsDemo = () => {
    sessionStorage.setItem(TOKEN_KEY, "demo-token");
    sessionStorage.setItem(USER_KEY, JSON.stringify(DEMO_USER));
    setToken("demo-token");
    setUser(DEMO_USER);
  };

  const logout = async () => {
    // Clear the session IMMEDIATELY so the UI logs out instantly — never wait
    // on the network (that caused the intermittent 5-6s "still logged in" lag).
    const uid = user?.id;
    clearSession();
    clearAllGameSaves();
    setUser(null);
    setToken(null);
    analytics.logout();
    resetIdentity();
    // Tell the server in the background (best-effort, non-blocking).
    if (uid) { axios.post(`${API}/npc/owl-answer-reset`, { user_id: uid }).catch(() => {}); }
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  };

  // Cross-tab auth sync. When any other tab writes/removes the token in
  // localStorage, the browser fires a `storage` event on every OTHER tab
  // of the same origin. We listen for that and update this tab's state
  // in place, so:
  //   - Sign in on Tab A → Tab B gets the user instantly (no refresh needed).
  //   - Sign out on Tab A → Tab B clears its user too (no ghost sessions).
  // The event only fires cross-tab (never on the tab that wrote), which
  // is exactly what we want.
  useEffect(() => {
    const onStorage = (e) => {
      if (getPreviewFromLocation()) return; // never hijack a preview session
      if (e.key !== TOKEN_KEY) return;
      if (e.newValue) {
        // Another tab just signed in, pick up the fresh token + user.
        try {
          const savedUser = JSON.parse(localStorage.getItem(USER_KEY) || "null");
          if (savedUser) {
            setToken(e.newValue);
            setUser(savedUser);
            setAuthStatus("ready");
          }
        } catch { /* ignore */ }
      } else {
        // Another tab signed out, drop our in-memory session too.
        setToken(null);
        setUser(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Render a minimal bootstrap splash only when the UX would actually
  // benefit from it: no cached user yet AND we're still waiting on
  // /auth/me. Once either fires (ready OR expired) we yield to the full
  // app render below (which handles the signup redirect for expired).
  if (authStatus === "loading" && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1428]" data-testid="app-auth-loading">
        <div className="animate-pulse text-white/40 text-sm font-fredoka">Loading your world…</div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ user, token, authStatus, setUser, refreshUser, login, loginAsDemo, logout, sounds, isPreviewSession: !!user?.preview_session }}>
      <HelmetProvider>
      <BrowserRouter>
        <IsaiahProvider>
          <GuestProvider>
          <ScrollToTop />
          <RouteSEO />
          <PreviewSessionBanner />
          <PreviewBannerBar />
          <AppShell
            user={user}
            canShowInstall={canShowInstall}
            triggerInstall={triggerInstall}
            dismissInstall={dismissInstall}
            dismissInstallForever={dismissInstallForever}
            isOnline={isOnline}
            wasOffline={wasOffline}
            retryQueue={retryQueue}
            showWhatsNew={showWhatsNew}
            setShowWhatsNew={setShowWhatsNew}
          />
          </GuestProvider>
        </IsaiahProvider>
      </BrowserRouter>
      </HelmetProvider>
    </AppContext.Provider>
  );
}
